import { Badge } from "@/components/ui/badge";
import { Building, CheckCircle, Rocket, Clock } from "lucide-react";

const STAGES = [
  { value: "all", label: "All", icon: Building },
  { value: "new-launch", label: "New Launch", icon: Rocket },
  { value: "under-construction", label: "Under Construction", icon: Clock },
  { value: "ready-to-move", label: "Ready to Move", icon: CheckCircle },
] as const;

export type PossessionStage = (typeof STAGES)[number]["value"];

interface PossessionStageFilterProps {
  value: PossessionStage;
  onChange: (stage: PossessionStage) => void;
}

export const PossessionStageFilter = ({ value, onChange }: PossessionStageFilterProps) => {
  return (
    <div className="flex flex-wrap gap-2">
      {STAGES.map((stage) => {
        const Icon = stage.icon;
        const isActive = value === stage.value;
        return (
          <Badge
            key={stage.value}
            variant={isActive ? "default" : "outline"}
            className={`px-3 py-2 text-sm cursor-pointer transition-colors gap-1.5 ${
              isActive
                ? "bg-primary text-primary-foreground"
                : "hover:bg-primary/10 hover:text-primary border-border/60"
            }`}
            onClick={() => onChange(stage.value)}
          >
            <Icon className="h-3.5 w-3.5" />
            {stage.label}
          </Badge>
        );
      })}
    </div>
  );
};

/**
 * Filter properties by possession stage based on possession_date field.
 */
export const filterByPossessionStage = <
  T extends { possession_date?: string | null; possession_stage?: string | null },
>(
  properties: T[],
  stage: PossessionStage
): T[] => {
  if (stage === "all") return properties;

  return properties.filter((p) => {
    // Primary: use the explicit possession_stage column from backend
    if (p.possession_stage) {
      return p.possession_stage === stage;
    }

    // Fallback: derive from possession_date for properties without explicit stage
    const now = new Date();
    const date = p.possession_date ? new Date(p.possession_date) : null;

    switch (stage) {
      case "ready-to-move":
        return !date || date <= now;
      case "under-construction":
        return date != null && date > now;
      case "new-launch": {
        if (!date) return false;
        const twoYearsFromNow = new Date();
        twoYearsFromNow.setFullYear(twoYearsFromNow.getFullYear() + 2);
        return date > twoYearsFromNow;
      }
      default:
        return true;
    }
  });
};

/**
 * Returns a human-readable label for a possession_stage value.
 */
export const getPossessionStageLabel = (stage: string | null | undefined): string | null => {
  switch (stage) {
    case "new-launch": return "New Launch";
    case "under-construction": return "Under Construction";
    case "ready-to-move": return "Ready to Move";
    default: return null;
  }
};
